import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonBase,
  Checkbox,
  IconButton,
  TextField,
  Box,
  MenuItem,
  Alert,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  WarningAmber,
  Add as AddIcon,
  Close,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { debounce } from 'lodash';
import api, { appointmentApi, clientApi, horseApi, Client, Horse } from '../../utils/api';
import { patientLabel } from '../../utils/patientLabel';
import { serviceCatalogService, ServiceCatalogItem } from '../../services/serviceCatalogService';
import { barnsService } from '../../services/barnsService';
import HorseFormDialog from '../../components/HorseFormDialog';
import EquipmentWindowsPicker, { buildEquipmentPayload } from '../../components/EquipmentWindowsPicker';
import { executeWrite, OfflineWriteBlockedError, withOfflineFallback, readClientsPage, readHorsesPage, readVeterinarians, skipWhenOffline, useIsOffline } from '../../offline';
import { SectionLabel, Segmented, WhenFields, ProviderPicker, TypeSelect, useProviderAvailability } from './scheduleFormParts';
import { getAppointmentColor } from './calendarGrid';

interface Veterinarian {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  professionalTitle?: string;
}

interface AddAppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  selectedDuration?: number;
  preSelectedVetId?: string;
  /** Seed (and with lockSubject, fix) the "Book for" subject — the Barn page
   *  opens this dialog locked to a barn-level booking. */
  initialSubject?: 'horse' | 'client' | 'barn';
  lockSubject?: boolean;
  preSelectedBarnId?: string;
  /** Display name for preSelectedBarnId so the locked field can render before
   *  (or without) the barns query resolving. */
  preSelectedBarnName?: string;
  /** The Schedule's scheduleControls().newOwner / .newPatient (clients and horses create). */
  canAddOwner?: boolean;
  canAddPatient?: boolean;
  /** Day by equipment: a slot click books that unit, so it starts ticked. */
  preSelectedEquipmentId?: string;
}


/**
 * New appointment (Schedule canvas board "Book appointment"), in a centred window. Patient, owner
 * only, or a barn with its horses ticked (one barn visit, an appointment per horse). The Barn page
 * opens it locked to its barn.
 */
const AddAppointmentDialog: FC<AddAppointmentDialogProps> = ({
  open,
  onClose,
  selectedDate,
  selectedDuration = 60,
  preSelectedVetId = '',
  initialSubject = 'horse',
  lockSubject = false,
  preSelectedBarnId = '',
  preSelectedBarnName = '',
  canAddOwner = false,
  canAddPatient = false,
  preSelectedEquipmentId = '',
}) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [date, setDate] = useState<Date | null>(selectedDate);
  const [duration, setDuration] = useState<number>(selectedDuration); // Use passed duration
  const [type, setType] = useState('');
  const [location, setLocation] = useState('clinic');
  // Subject: who/what the appointment is for — a patient (today's behavior),
  // an owner (client), or a whole barn. Owner/barn bookings are online-only
  // (the sync push strips their FK columns — INV-43).
  const [subject, setSubject] = useState<'horse' | 'client' | 'barn'>(initialSubject);
  const [horseId, setHorseId] = useState('');
  const [selectedHorse, setSelectedHorse] = useState<Horse | null>(null);
  const [horseSearchValue, setHorseSearchValue] = useState('');
  const [horseSearchQuery, setHorseSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedBarnId, setSelectedBarnId] = useState(preSelectedBarnId);
  // Barn subject: the barn's horses ticked for this visit (none = a barn-level booking).
  const [pickedHorseIds, setPickedHorseIds] = useState<string[]>([]);
  const [status, setStatus] = useState<'tentative' | 'scheduled'>('scheduled');
  const [notes, setNotes] = useState('');
  const [assignedVetId, setAssignedVetId] = useState('');
  // equipment: selected item IDs
  const isOffline = useIsOffline();
  const [equipmentIds, setEquipmentIds] = useState<string[]>([]);
  // per-item occupied window: defaults to appointment start/end when item is selected
  const [equipmentWindows, setEquipmentWindows] = useState<
    Record<string, { startTime: Date; endTime: Date }>
  >({});
  // Per-unit "All day": the window becomes the appointment's whole calendar
  // day, computed at submit (see EquipmentWindowsPicker/buildEquipmentPayload).
  const [equipmentAllDay, setEquipmentAllDay] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [isAddPatientOpen, setIsAddPatientOpen] = useState(false);
  const [newOwner, setNewOwner] = useState<{ name: string; phone: string; email: string } | null>(null);

  useEffect(() => {
    setDate(selectedDate);
    setDuration(selectedDuration);
    setAssignedVetId(preSelectedVetId);
    setEquipmentIds(preSelectedEquipmentId ? [preSelectedEquipmentId] : []);
    // The picker shows a unit's window editor only when it has a window, as its own tick seeds one.
    setEquipmentWindows(
      preSelectedEquipmentId && selectedDate
        ? { [preSelectedEquipmentId]: { startTime: selectedDate, endTime: new Date(selectedDate.getTime() + selectedDuration * 60_000) } }
        : {},
    );
    setEquipmentAllDay({});
  }, [selectedDate, selectedDuration, preSelectedVetId, preSelectedEquipmentId]);

  // Stable primitives: on the calendar these never change ('horse'/''), on the
  // Barn page they're fixed per barn — so this can't clobber an in-progress form.
  useEffect(() => {
    setSubject(initialSubject);
    setSelectedBarnId(preSelectedBarnId);
  }, [initialSubject, preSelectedBarnId]);

  // A different barn has different horses.
  useEffect(() => {
    setPickedHorseIds([]);
  }, [selectedBarnId]);

  // Debounced search for horses
  const debouncedHorseSearch = useCallback(
    debounce((value: string) => {
      setHorseSearchQuery(value);
    }, 300),
    []
  );

  // Fetch horses with search. Gated on the patient subject: a barn-locked
  // dialog (Barn page) never uses horses.
  const { data: horses = [], isLoading: isLoadingHorses } = useQuery<Horse[]>(
    ['horses-search', horseSearchQuery],
    withOfflineFallback(
      () => horseApi.list(horseSearchQuery),
      () => readHorsesPage({ page: 1, limit: 500, search: horseSearchQuery }).then((p) => p.data as Horse[]),
    ),
    {
      enabled: open && subject === 'horse',
      keepPreviousData: true,
    }
  );

  const { data: veterinarians = [] } = useQuery<Veterinarian[]>(
    'veterinarians',
    withOfflineFallback(
      async () => {
        const response = await api.get('/users/veterinarians');
        return response.data;
      },
      () => readVeterinarians() as Promise<Veterinarian[]>,
    ),
  );

  // Owner subject: clients for the autocomplete (same key/fallback as the
  // patient form's owner picker).
  const { data: clients = [] } = useQuery<Client[]>(
    ['clients-autocomplete'],
    withOfflineFallback(
      () => clientApi.list(),
      () => readClientsPage({ page: 1, limit: 1000 }).then((p) => p.data as Client[]),
    ),
    { enabled: open && subject === 'client', staleTime: 30_000 },
  );

  // Barn subject: active barns. Online-only (barns aren't in the replica, and
  // barn bookings can't queue anyway).
  const { data: barns = [] } = useQuery(
    ['barns'],
    () => barnsService.list(),
    { enabled: open && subject === 'barn' && !isOffline, staleTime: 60_000 },
  );

  // The picked barn's address, contact and horses (its roster).
  const { data: barnDetail } = useQuery(
    ['barn', selectedBarnId],
    () => barnsService.get(selectedBarnId),
    { enabled: open && subject === 'barn' && !!selectedBarnId && !isOffline, staleTime: 60_000 },
  );
  const barnHorses = useMemo(
    () => (barnDetail?.horses ?? []).filter((h) => (h.status ?? 'ACTIVE').toUpperCase() === 'ACTIVE'),
    [barnDetail],
  );

  // The Barn page passes its barn by id before (or without) the barns query
  // resolving — inject it so the locked select can always render its name.
  const barnOptions = useMemo(() => {
    if (
      preSelectedBarnId &&
      !barns.some((b: { id: string }) => b.id === preSelectedBarnId)
    ) {
      return [{ id: preSelectedBarnId, name: preSelectedBarnName || '…' }, ...barns];
    }
    return barns;
  }, [barns, preSelectedBarnId, preSelectedBarnName]);

  const { data: catalogItems = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', 'appointment'],
    () => serviceCatalogService.getAll({ category: 'appointment' }),
    { enabled: open }
  );

  // Derived appointment end time
  const appointmentEnd = useMemo(() => {
    if (!date) return null;
    return new Date(date.getTime() + duration * 60_000);
  }, [date, duration]);

  // Fetch all equipment (available + unavailable) whenever dialog opens or date/duration changes
  const { data: equipmentItems = [] } = useQuery<
    Array<{ id: string; name: string; type: string; available: boolean }>
  >(
    ['equipment-availability', date?.toISOString(), duration, assignedVetId],
    async () => {
      // If no date yet, fetch without an overlap window so all equipment shows as available
      const start = date ? date.toISOString() : new Date().toISOString();
      const end   = appointmentEnd ? appointmentEnd.toISOString() : new Date(Date.now() + 60 * 60_000).toISOString();
      const params = new URLSearchParams({ start, end });
      // A unit held by THIS vet stays selectable (reuse); only other vets' overlaps block it.
      if (assignedVetId) params.set('assignedVetId', assignedVetId);
      const response = await api.get(`/equipment/available?${params}`);
      return response.data;
    },
    { enabled: open && skipWhenOffline(), keepPreviousData: true },
  );

  const typeOptions = useMemo(() => {
    const items = catalogItems.filter((item) => item.isActive);
    const names = items.length > 0
      ? items.map((item) => ({ name: item.name, color: item.color }))
      : ['Performance Evaluation', 'PPE (Pre-Purchase Exam)', 'Vaccination', 'Dental Care', 'Emergency', 'Surgery'].map((name) => ({ name, color: null as string | null | undefined }));
    // A type with no colour of its own takes the Schedule's default for its name, as on the grid.
    return names.map((t) => ({ name: t.name, color: t.color || getAppointmentColor({ type: t.name }) }));
  }, [catalogItems]);

  const busy = useProviderAvailability(open ? date : null, duration);
  // The rule Edit and drag already apply: no booking over the provider's other appointment, their
  // blocked time or a closure. Same data the provider list shows, so it holds offline too.
  const clash = busy['*'] ?? (assignedVetId ? busy[assignedVetId] : undefined);
  const clashMessage = !clash
    ? null
    : busy['*']
      ? `${clash}. Pick another time.`
      : `${veterinarians.find((v) => v.id === assignedVetId)?.name ?? 'This provider'} isn't free then (${clash.replace(/^Busy · /, '')}). Pick another time or provider.`;

  const onBookingSuccess = async () => {
    await queryClient.invalidateQueries(['appointments']);
    await queryClient.invalidateQueries('appointments');
    await queryClient.refetchQueries(['appointments']);

    onClose();
    resetForm();
  };

  const createAppointment = useMutation(
    async (data: {
      date: string;
      duration?: number;
      timeZone: string;
      type: string;
      location: string;
      horseId: string;
      notes?: string;
      assignedVetId?: string;
      status?: string;
      equipment?: Array<{ id: string; startTime: string; endTime: string }>;
    }) => {
      // Equipment reservations are online-only: the offline replay path has
      // no availability confirmation (and the push handler strips the field).
      // The QUEUED payload never carries equipment — covering both offline
      // creates AND the connectivity-fallthrough where the online POST fails
      // and the op falls back to the queue. The online networkFn sends the
      // full payload. The picker is disabled offline, so a queued create
      // normally has no equipment to lose.
      const { equipment: _omitted, ...queuedPayload } = data;
      const response = await executeWrite({
        entityType: 'appointment',
        table: 'appointments',
        payload: queuedPayload,
        networkFn: () => api.post('/appointments', data).then(r => r.data),
      });
      return response;
    },
    {
      onSuccess: onBookingSuccess,
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError) {
          setError(error.message);
          return;
        }
        setError(error.response?.data?.message || 'Failed to create appointment');
      },
    }
  );

  // Owner-subject bookings are online-only by design: the sync push strips
  // clientId (INV-43), so a queued op would arrive subject-less. Direct POST,
  // never executeWrite — fail loud offline (the toggle is disabled anyway).
  const createOwnerAppointment = useMutation(
    (data: {
      date: string;
      duration?: number;
      timeZone: string;
      type: string;
      location: string;
      clientId: string;
      notes?: string;
      assignedVetId?: string;
      status?: string;
      equipment?: Array<{ id: string; startTime: string; endTime: string }>;
    }) => api.post('/appointments', data).then((r) => r.data),
    {
      onSuccess: onBookingSuccess,
      onError: (error: any) =>
        setError(error.response?.data?.message || 'Failed to create appointment'),
    },
  );

  // Barn bookings use the barn-visit endpoint: no horses = one barn-level appointment; ticked
  // horses = one appointment each, shown on the Schedule as one barn visit.
  const createBarnBooking = useMutation(
    (data: Parameters<typeof appointmentApi.createBarnVisit>[0]) =>
      appointmentApi.createBarnVisit(data),
    {
      onSuccess: onBookingSuccess,
      onError: (error: any) =>
        setError(error.response?.data?.message || 'Failed to create appointment'),
    },
  );

  // "New owner" while on the phone: online only, like an owner booking.
  const createOwner = useMutation(
    (data: { name: string; phone?: string; email?: string }) => clientApi.create(data),
    {
      onSuccess: (client: Client) => {
        queryClient.invalidateQueries(['clients-autocomplete']);
        setSelectedClient(client);
        setSubject('client');
        setNewOwner(null);
      },
      onError: (e: any) => setError(e?.response?.data?.message || 'Could not add the owner.'),
    },
  );

  const resetForm = () => {
    setDate(null);
    setDuration(selectedDuration);
    setType('');
    setLocation('clinic');
    setSubject(initialSubject);
    setHorseId('');
    setSelectedHorse(null);
    setHorseSearchValue('');
    setHorseSearchQuery('');
    setSelectedClient(null);
    setSelectedBarnId(preSelectedBarnId);
    setPickedHorseIds([]);
    setStatus('scheduled');
    setNotes('');
    setAssignedVetId(preSelectedVetId);
    setEquipmentIds([]);
    setEquipmentWindows({});
    setEquipmentAllDay({});
    setError(null);
    setIsAddPatientOpen(false);
    setNewOwner(null);
  };

  const handleHorseSearchChange = (event: React.SyntheticEvent, value: string) => {
    setHorseSearchValue(value);
    debouncedHorseSearch(value);
  };

  const ADD_NEW_PATIENT_ID = '__add_new_patient__';

  const handleHorseSelect = (event: React.SyntheticEvent, value: Horse | null) => {
    if (value && value.id === ADD_NEW_PATIENT_ID) {
      setIsAddPatientOpen(true);
      return;
    }
    setSelectedHorse(value);
    setHorseId(value?.id || '');
  };

  const handlePatientCreated = (newHorse: Horse) => {
    // HorseFormDialog refreshes ['horses'] only; this picker reads its own key.
    queryClient.invalidateQueries(['horses-search']);
    setSubject('horse');
    setSelectedHorse(newHorse);
    setHorseId(newHorse.id);
    setHorseSearchValue(newHorse.name);
    setIsAddPatientOpen(false);
  };

  const handleSubmit = () => {
    if (!date || !type) return;
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // All-day units span the appointment's calendar day; explicit windows are
    // sent as-is. Computed here (not stored) so a date change never leaves a
    // stale all-day window.
    const equipment = buildEquipmentPayload(
      equipmentIds,
      equipmentWindows,
      equipmentAllDay,
      date,
      appointmentEnd ?? new Date(date.getTime() + duration * 60_000),
    );
    const shared = {
      duration,
      timeZone,
      type,
      notes: notes || undefined,
      assignedVetId: assignedVetId || undefined,
      status,
      equipment,
    };

    if (subject === 'horse') {
      if (!horseId || !location) return;
      createAppointment.mutate({ ...shared, date: date.toISOString(), location, horseId });
    } else if (subject === 'client') {
      if (!selectedClient || !location) return;
      createOwnerAppointment.mutate({
        ...shared,
        date: date.toISOString(),
        location,
        clientId: selectedClient.id,
      });
    } else {
      if (!selectedBarnId) return;
      // Barn route: server sets location = barn name; date rides as a local
      // wall-clock string + timeZone (the barn-visit endpoint's convention).
      createBarnBooking.mutate({
        barnId: selectedBarnId,
        horseIds: pickedHorseIds,
        date: format(date, "yyyy-MM-dd'T'HH:mm"),
        timeZone,
        duration,
        type: type || undefined,
        assignedVetId: assignedVetId || undefined,
        equipment,
        notes: notes || undefined,
        status,
      });
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isBooking =
    createAppointment.isLoading || createOwnerAppointment.isLoading || createBarnBooking.isLoading;
  const pickedCount = pickedHorseIds.length;
  const bookLabel = subject === 'barn'
    ? `Book barn visit${pickedCount > 0 ? ` · ${pickedCount} ${pickedCount === 1 ? 'horse' : 'horses'}` : ''}`
    : 'Book appointment';
  const criticalAlerts = (selectedHorse?.medicalAlerts ?? []).filter((alert: any) => alert?.isCritical);
  const otherAlerts = (selectedHorse?.medicalAlerts ?? []).filter((alert: any) => !alert?.isCritical);
  const barnAddress = barnDetail
    ? [barnDetail.street, [barnDetail.city, [barnDetail.state, barnDetail.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(', ')
    : '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{
        'aria-label': 'New appointment',
        sx: { width: 560, maxWidth: '100%', height: { sm: 'calc(100% - 64px)' }, maxHeight: { sm: 900 }, display: 'flex', flexDirection: 'column', borderRadius: { sm: 3 } },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 2, borderBottom: '1px solid #F0F1F3' }}>
        <Typography component="h2" sx={{ flex: 1, m: 0, fontSize: '1.1rem', fontWeight: 700, color: '#3B2E1A' }}>
          New appointment
        </Typography>
        <IconButton aria-label="Close without booking" onClick={handleClose} sx={{ width: 36, height: 36 }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.75 }}>
        {error && <Alert severity="error">{error}</Alert>}

        {/* Who the appointment is for. Owner/barn bookings are online-only (their links can't ride
            the offline queue, INV-43). Hidden when the caller locked the subject (Barn page). */}
        <Box>
          <SectionLabel>Book for</SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {!lockSubject && (
              <Segmented
                label="Book for"
                value={subject}
                onChange={setSubject}
                options={[
                  { value: 'horse', label: 'Patient' },
                  { value: 'client', label: 'Owner only', disabled: isOffline },
                  { value: 'barn', label: 'Barn', disabled: isOffline },
                ]}
              />
            )}
            {isOffline && !lockSubject && (
              <Typography variant="caption" color="text.secondary">Owner and barn bookings need a connection.</Typography>
            )}

            {subject === 'horse' && (
              <Autocomplete
                options={horses}
                value={selectedHorse}
                inputValue={horseSearchValue}
                onInputChange={handleHorseSearchChange}
                onChange={handleHorseSelect}
                getOptionLabel={(option) => option.id === ADD_NEW_PATIENT_ID ? '' : option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, params) => {
                  const filtered = options.filter((option) =>
                    patientLabel(option).toLowerCase().includes(params.inputValue.toLowerCase())
                  );
                  if (canAddPatient) {
                    filtered.push({
                      id: ADD_NEW_PATIENT_ID,
                      name: 'Add New Patient',
                      breed: '',
                      dateOfBirth: '',
                      gender: '',
                      color: '',
                      owners: [],
                    } as Horse);
                  }
                  return filtered;
                }}
                loading={isLoadingHorses}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search patients or owners"
                    inputProps={{ ...params.inputProps, 'aria-label': 'Patient' }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {isLoadingHorses ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  if (option.id === ADD_NEW_PATIENT_ID) {
                    return (
                      <li {...props} key={option.id}>
                        <Box display="flex" alignItems="center" gap={1} width="100%" sx={{ color: 'primary.main', borderTop: '1px solid', borderColor: 'divider', pt: 1, fontWeight: 600 }}>
                          <AddIcon fontSize="small" />
                          <Typography variant="body2" fontWeight={600} color="primary.main">Add New Patient</Typography>
                        </Box>
                      </li>
                    );
                  }
                  return (
                    <li {...props} key={option.id}>
                      <Box display="flex" flexDirection="column" width="100%">
                        <Typography variant="body2" fontWeight={600}>{patientLabel(option)}</Typography>
                        {option.owners && option.owners.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Owner: {typeof option.owners[0] === 'string' ? option.owners[0] : option.owners[0].name}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                fullWidth
              />
            )}

            {subject === 'client' && (
              <Autocomplete
                options={clients}
                value={selectedClient}
                onChange={(_e, value) => setSelectedClient(value)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box display="flex" flexDirection="column" width="100%">
                      <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                      {(option.email || option.phone) && (
                        <Typography variant="caption" color="text.secondary">
                          {[option.email, option.phone].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search owners"
                    inputProps={{ ...params.inputProps, 'aria-label': 'Owner' }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                fullWidth
              />
            )}

            {subject === 'barn' && (
              <TextField
                label="Barn"
                select
                size="small"
                value={selectedBarnId}
                onChange={(e) => setSelectedBarnId(e.target.value)}
                fullWidth
                disabled={!!preSelectedBarnId}
                helperText={isOffline ? 'Barn bookings need a connection.' : undefined}
              >
                {barnOptions.length === 0 ? (
                  <MenuItem disabled>
                    <em>No barns yet. Add one on the Barns page.</em>
                  </MenuItem>
                ) : (
                  barnOptions.map((barn: { id: string; name: string }) => (
                    <MenuItem key={barn.id} value={barn.id}>{barn.name}</MenuItem>
                  ))
                )}
              </TextField>
            )}

            {!lockSubject && (canAddOwner || canAddPatient) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, fontSize: '0.82rem', color: 'text.secondary' }}>
                <span>On the phone with someone new?</span>
                {canAddOwner && (
                  <ButtonBase
                    onClick={() => setNewOwner({ name: '', phone: '', email: '' })}
                    disabled={isOffline}
                    sx={{ gap: 0.5, height: 30, px: 1.25, borderRadius: 999, bgcolor: '#FCF0E3', color: '#7A4314', fontSize: '0.82rem', fontWeight: 600 }}
                  >
                    <AddIcon sx={{ fontSize: 14 }} /> New owner
                  </ButtonBase>
                )}
                {canAddPatient && (
                  <ButtonBase
                    onClick={() => setIsAddPatientOpen(true)}
                    sx={{ gap: 0.5, height: 30, px: 1.25, borderRadius: 999, bgcolor: '#FCF0E3', color: '#7A4314', fontSize: '0.82rem', fontWeight: 600 }}
                  >
                    <AddIcon sx={{ fontSize: 14 }} /> New patient
                  </ButtonBase>
                )}
              </Box>
            )}

            {subject === 'barn' && barnDetail && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1.5, border: '1px solid', borderColor: 'primary.main', bgcolor: '#FFFBF6', borderRadius: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarehouseIcon sx={{ fontSize: 16, color: '#7A4314' }} />
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{barnDetail.name}</Typography>
                </Box>
                {barnAddress && <Typography sx={{ pl: 3, fontSize: '0.82rem', color: '#374151' }}>{barnAddress}</Typography>}
                {(barnDetail.contactName || barnDetail.contactPhone) && (
                  <Typography sx={{ pl: 3, fontSize: '0.82rem', color: '#374151' }}>
                    {`Contact: ${[barnDetail.contactName, barnDetail.contactPhone].filter(Boolean).join(' · ')}`}
                  </Typography>
                )}
                {barnDetail.notes && <Typography sx={{ pl: 3, fontSize: '0.82rem', color: '#374151' }}>{barnDetail.notes}</Typography>}
              </Box>
            )}

            {subject === 'horse' && criticalAlerts.length > 0 && (
              <Alert severity="error" icon={<WarningAmber />}>
                {criticalAlerts.map((alert: any, index: number) => (
                  <Typography key={alert.id || index} variant="body2" fontWeight={700}>{alert.text || alert}</Typography>
                ))}
              </Alert>
            )}
            {subject === 'horse' && otherAlerts.length > 0 && (
              <Alert severity="warning" icon={<WarningAmber />}>
                {otherAlerts.map((alert: any, index: number) => (
                  <Typography key={alert.id || index} variant="body2">{alert.text || alert}</Typography>
                ))}
              </Alert>
            )}
          </Box>
        </Box>

        {subject === 'barn' && barnDetail && (
          <Box component="fieldset" sx={{ m: 0, p: 0, border: 0 }}>
            <SectionLabel aside="Optional">Horses at this barn</SectionLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {barnHorses.length === 0 && (
                <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>No active horses at this barn.</Typography>
              )}
              {barnHorses.map((h) => {
                const on = pickedHorseIds.includes(h.id);
                const alert = (h.medicalAlerts ?? []).find((a) => a?.isCritical) ?? (h.medicalAlerts ?? [])[0];
                return (
                  <Box
                    key={h.id}
                    component="label"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 0.75, border: '1px solid', borderColor: on ? 'primary.main' : '#E5E7EB', bgcolor: on ? '#FFFBF6' : 'background.paper', borderRadius: 2.5, cursor: 'pointer' }}
                  >
                    <Checkbox
                      size="small"
                      checked={on}
                      onChange={() => setPickedHorseIds((ids) => (on ? ids.filter((id) => id !== h.id) : [...ids, h.id]))}
                      inputProps={{ 'aria-label': h.name }}
                      sx={{ p: 0.5 }}
                    />
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.88rem', fontWeight: 600 }} noWrap>
                      {h.name}
                      {h.owners?.[0]?.name && <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>{` · ${h.owners[0].name}`}</Box>}
                    </Typography>
                    {alert?.text && (
                      <Box component="span" title={alert.text} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem', fontWeight: 600, color: '#B42318' }}>
                        <WarningAmber sx={{ fontSize: 14 }} />
                        {alert.text}
                      </Box>
                    )}
                  </Box>
                );
              })}
              <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.45, color: '#374151' }}>
                {pickedCount === 0
                  ? 'No horses ticked. This books the barn itself, and you can add horses later.'
                  : `${pickedCount} ${pickedCount === 1 ? 'horse' : 'horses'}. Each gets its own appointment and record, and the Schedule shows them as one barn visit.`}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Barn bookings show the barn's name as the location (server-set). */}
        {subject !== 'barn' && (
          <Box>
            <SectionLabel>Where</SectionLabel>
            <Segmented
              label="Location"
              value={location === 'farm' ? 'farm' : 'clinic'}
              onChange={setLocation}
              options={[{ value: 'clinic', label: 'Clinic' }, { value: 'farm', label: 'Farm call' }]}
            />
          </Box>
        )}

        <Box>
          <SectionLabel aside={subject === 'barn' && pickedCount > 1 ? 'Same for every horse' : undefined}>Type</SectionLabel>
          <TypeSelect types={typeOptions} value={type} onChange={setType} />
        </Box>

        <Box>
          <SectionLabel>When</SectionLabel>
          <WhenFields
            start={date}
            minutes={duration}
            lengths={[30, 45, 60, 90, 120, 180]}
            onChange={(next, minutes) => {
              setDate(next);
              setDuration(minutes);
            }}
          />
        </Box>

        <Box>
          <SectionLabel>Provider · free at this time?</SectionLabel>
          <ProviderPicker
            providers={veterinarians.map((v) => ({ id: v.id, name: v.name }))}
            value={assignedVetId}
            onChange={setAssignedVetId}
            busy={busy}
          />
          {clashMessage && <Alert severity="error" sx={{ mt: 1 }}>{clashMessage}</Alert>}
        </Box>

        {/* Equipment Needed: availability is server-checked, so the picker is online-only;
            offline creates queue without equipment. */}
        <Box>
          <SectionLabel>Equipment</SectionLabel>
          <EquipmentWindowsPicker
            items={equipmentItems}
            selectedIds={equipmentIds}
            onSelectedIdsChange={setEquipmentIds}
            windows={equipmentWindows}
            onWindowsChange={setEquipmentWindows}
            allDayById={equipmentAllDay}
            onAllDayChange={setEquipmentAllDay}
            disabled={isOffline}
            helperText={isOffline ? 'Equipment scheduling needs a connection' : undefined}
            appointmentStart={date}
            appointmentEnd={appointmentEnd}
          />
        </Box>

        <Box>
          <SectionLabel>Status</SectionLabel>
          <Segmented
            label="Status"
            value={status}
            onChange={setStatus}
            options={[{ value: 'tentative', label: 'Tentative' }, { value: 'scheduled', label: 'Confirmed' }]}
          />
        </Box>

        <TextField
          label="Notes"
          placeholder="Anything the vet should know before arriving"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={3}
          fullWidth
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.75, borderTop: '1px solid #F0F1F3' }}>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={handleClose} sx={{ textTransform: 'none', fontWeight: 600, color: '#3B2E1A', borderColor: '#D1D5DB', height: 40 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', height: 40 }}
          disabled={
            !date ||
            !type ||
            isBooking ||
            !!clash ||
            (subject === 'horse' && (!location || !horseId)) ||
            (subject === 'client' && (!location || !selectedClient)) ||
            (subject === 'barn' && !selectedBarnId) ||
            // Owner/barn bookings are online-only; with the toggle hidden
            // (lockSubject) this is the only thing failing closed offline.
            (subject !== 'horse' && isOffline)
          }
        >
          {bookLabel}
        </Button>
      </Box>

      <HorseFormDialog
        open={isAddPatientOpen}
        onClose={() => setIsAddPatientOpen(false)}
        onSaved={handlePatientCreated}
      />

      <Dialog open={!!newOwner} onClose={() => setNewOwner(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New owner</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField label="Name" required autoFocus value={newOwner?.name ?? ''} onChange={(e) => setNewOwner((o) => o && { ...o, name: e.target.value })} />
          <TextField label="Phone" value={newOwner?.phone ?? ''} onChange={(e) => setNewOwner((o) => o && { ...o, phone: e.target.value })} />
          <TextField label="Email" type="email" value={newOwner?.email ?? ''} onChange={(e) => setNewOwner((o) => o && { ...o, email: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOwner(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newOwner?.name.trim() || createOwner.isLoading}
            onClick={() => newOwner && createOwner.mutate({
              name: newOwner.name.trim(),
              phone: newOwner.phone.trim() || undefined,
              email: newOwner.email.trim() || undefined,
            })}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            Add owner
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AddAppointmentDialog;
